/* =====================================================================
   INPUT — клавиатура, мышь и СЕНСОР (плавающий джойстик).
   На телефоне: первый палец = база джойстика, драг от базы = вектор
   движения; тап = клик по UI. Без библиотек.
   ===================================================================== */

import { Game } from './game';

export const Input: any = {
  keys: {},
  mouseX: 0, mouseY: 0,
  mouseDown: false,
  clicked: false,        // одиночный клик/тап в этом кадре (UI)
  pressed: {},           // нажатия-в-этом-кадре (для меню)
  // сенсор
  isTouch: false,
  touchId: null,
  touchActive: false,
  baseX: 0, baseY: 0, curX: 0, curY: 0,
  deadzone: 14,
  maxRadius: 72,
  // click-to-move: засекаем начало нажатия, чтобы отличить короткий клик («иди сюда») от удержания-steer
  _downAt: 0, _downX: 0, _downY: 0,

  init(canvas: any) {
    window.addEventListener('keydown', (e) => {
      const k = this._norm(e.key);
      if (!this.keys[k]) this.pressed[k] = true;
      this.keys[k] = true;
      if (['arrowup','arrowdown','arrowleft','arrowright',' '].includes(k)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => { this.keys[this._norm(e.key)] = false; });
    window.addEventListener('blur', () => { this.keys = {}; });

    canvas.addEventListener('mousemove', (e: any) => {
      const p = this._toGame(e.clientX, e.clientY, canvas);
      this.mouseX = p.x; this.mouseY = p.y;
    });
    canvas.addEventListener('mousedown', () => {
      this.mouseDown = true; this.clicked = true;
      this._downAt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      this._downX = this.mouseX; this._downY = this.mouseY;
    });
    window.addEventListener('mouseup', () => {
      // короткий клик без перетаскивания во время игры = click-to-move: назначить точку-цель + вспышку
      if (this.mouseDown && !this.isTouch && typeof Game !== 'undefined' && Game.state === 'playing' && Game.player) {
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const moved = Math.hypot(this.mouseX - this._downX, this.mouseY - this._downY);
        if (now - this._downAt <= 250 && moved <= 14) {
          const wx = Game.player.x + (this.mouseX - Game.viewW / 2);
          const wy = Game.player.y + (this.mouseY - Game.viewH / 2);
          Game.setMoveTarget(wx, wy);
        }
      }
      this.mouseDown = false;
    });

    // ----- сенсор -----
    canvas.addEventListener('touchstart', (e: any) => {
      e.preventDefault();
      this.isTouch = true;
      const t = e.changedTouches[0];
      const p = this._toGame(t.clientX, t.clientY, canvas);
      this.mouseX = p.x; this.mouseY = p.y;   // для UI-тапа (hover+click)
      this.clicked = true;
      if (this.touchId === null) {            // джойстик — первый палец
        this.touchId = t.identifier;
        this.touchActive = true;
        this.baseX = this.curX = p.x;
        this.baseY = this.curY = p.y;
      }
    }, { passive: false });

    canvas.addEventListener('touchmove', (e: any) => {
      e.preventDefault();
      for (const t of e.changedTouches) {
        if (t.identifier === this.touchId) {
          const p = this._toGame(t.clientX, t.clientY, canvas);
          this.curX = p.x; this.curY = p.y;
          this.mouseX = p.x; this.mouseY = p.y;
        }
      }
    }, { passive: false });

    const endTouch = (e: any) => {
      for (const t of e.changedTouches) {
        if (t.identifier === this.touchId) { this.touchId = null; this.touchActive = false; }
      }
    };
    canvas.addEventListener('touchend', endTouch);
    canvas.addEventListener('touchcancel', endTouch);
  },

  // клиентские координаты → игровые (логические px), не зависит от DPR
  _toGame(cx: number, cy: number, canvas: any) {
    const r = canvas.getBoundingClientRect();
    return {
      x: (cx - r.left) * (Game.viewW / r.width),
      y: (cy - r.top) * (Game.viewH / r.height),
    };
  },

  _norm(k: string) { return k.toLowerCase(); },

  // Вектор движения: сенсор → удержание ЛКМ (steer) → WASD/стрелки → click-to-move (авто-цель).
  // Любое ручное движение (джойстик/удержание/WASD) отменяет назначенную кликом точку.
  moveVector() {
    if (this.touchActive) {
      const dx = this.curX - this.baseX, dy = this.curY - this.baseY;
      const d = Math.hypot(dx, dy);
      if (d < this.deadzone) return { x: 0, y: 0 };
      if (typeof Game !== 'undefined') Game.moveTarget = null;
      const mag = Math.min(1, d / this.maxRadius);
      return { x: (dx / d) * mag, y: (dy / d) * mag };
    }
    // десктоп: УДЕРЖАНИЕ ЛКМ (после порога времени/драга, не короткий клик) = steer к курсору
    if (this.mouseDown && !this.isTouch && typeof Game !== 'undefined' && Game.state === 'playing') {
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const moved = Math.hypot(this.mouseX - this._downX, this.mouseY - this._downY);
      if (now - this._downAt > 250 || moved > 14) {     // это удержание/драг, а не клик-«иди сюда»
        Game.moveTarget = null;
        const dx = this.mouseX - Game.viewW / 2, dy = this.mouseY - Game.viewH / 2;
        const d = Math.hypot(dx, dy);
        if (d > 10) { const mag = Math.min(1, d / 110); return { x: (dx / d) * mag, y: (dy / d) * mag }; }
        return { x: 0, y: 0 };
      }
    }
    let x = 0, y = 0;
    if (this.keys['w'] || this.keys['arrowup']) y -= 1;
    if (this.keys['s'] || this.keys['arrowdown']) y += 1;
    if (this.keys['a'] || this.keys['arrowleft']) x -= 1;
    if (this.keys['d'] || this.keys['arrowright']) x += 1;
    if (x || y) {
      if (typeof Game !== 'undefined') Game.moveTarget = null;   // WASD перехватывает управление
      if (x && y) { const inv = 1 / Math.SQRT2; x *= inv; y *= inv; }
      return { x, y };
    }
    // CLICK-TO-MOVE: идём к назначенной точке, плавно тормозим у цели, по приходу — стоп
    if (typeof Game !== 'undefined' && Game.moveTarget && Game.player) {
      const dx = Game.moveTarget.x - Game.player.x, dy = Game.moveTarget.y - Game.player.y;
      const d = Math.hypot(dx, dy);
      if (d < 8) { Game.moveTarget = null; return { x: 0, y: 0 }; }
      const mag = d < 46 ? d / 46 : 1;   // плавное торможение у точки
      return { x: (dx / d) * mag, y: (dy / d) * mag };
    }
    return { x: 0, y: 0 };
  },

  wasPressed(k: string) { return !!this.pressed[k]; },

  // вызвать в конце кадра
  endFrame() {
    this.clicked = false;
    this.pressed = {};
  },
};
